import type { Step, WorkflowResult } from '@mastra/core/workflows';
import { useMutation } from '@tanstack/react-query';
import type { ZodObject, ZodRawShape, ZodType } from 'zod';

import { captureException } from '../../logger/Logger';
import { useMastraClient } from '../providers/MastraClient';

export interface WorkflowRunParams {
    workflowId: string;
    inputData: Record<string, unknown>;
}

export const useRunWorkflow = <
    TState extends ZodObject<ZodRawShape> = ZodObject<ZodRawShape>,
    TInput extends ZodType = ZodType,
    TOutput extends ZodType = ZodType,
    TSteps extends Array<
        Step<string, ZodObject<ZodRawShape>, ZodType, ZodType>
    > = Array<Step<string, ZodObject<ZodRawShape>, ZodType, ZodType>>,
>() => {
    const mastraClient = useMastraClient();

    return useMutation<
        WorkflowResult<TState, TInput, TOutput, TSteps> | null,
        Error,
        WorkflowRunParams
    >({
        mutationFn: async ({
            workflowId,
            inputData,
        }): Promise<WorkflowResult<TState, TInput, TOutput, TSteps> | null> => {
            try {
                // Get the workflow.
                const workflow = mastraClient.getWorkflow(workflowId);

                // Create a run instance of the workflow.
                const run = await workflow.createRun();

                // Execute with workflow input
                return (await run.startAsync({
                    inputData,
                })) as WorkflowResult<TState, TInput, TOutput, TSteps>;
            } catch (e) {
                captureException(e);
                throw e;
            }
        },
    });
};
